class LineItems extends HTMLElement {
  section =
    window.location.pathname === `${Shopify.routes.root}cart`
      ? 'fetch-line-items-cart-page'
      : 'fetch-line-items';
  connectedCallback() {
    this.unsubscribeCartChange = subscribe('cart:change', ({ line, quantity }) =>
      this.cartChange(line, quantity)
    );
    this.unsubscribeRefreshCart = subscribe('cart:added', () => this.refreshCart());
  }

  disconnectedCallback() {
    this.unsubscribeCartChange();
    this.unsubscribeRefreshCart();
  }

  /**
   * Change the cart.
   * @description Use the /{locale}/cart/change.js endpoint to change the quantity, properties, and selling_plan properties of a cart line item. Only items already in your cart can be changed, and you can change only one line item at a time.
   * @tutorial https://shopify.dev/docs/api/ajax/reference/cart#post-locale-cart-change-js
   */
  async cartChange(line, quantity) {
    publish('cart:updating', {
      line,
    });

    const body = JSON.stringify({
      line,
      quantity,
      sections: this.section,
      sections_url: window.location.pathname,
    });

    try {
      const { error, data } = await this.fetchCart(routes.cart_change_url, body);

      if (error) throw new Error(error);
      this.renderSections(data.sections);

      publish('cart:updated', {
        itemCount: data.item_count,
      });

      window.toast.success('Cart updated');
    } catch (error) {
      console.error(error);
      window.toast.error(error.message);

      publish('cart:error', {
        line,
      });
    }
  }

  /**
   * Fetch the cart.
   * @param {string} input - The URL to fetch.
   * @param {string} body - The request body.
   * @returns {Promise} The response.
   */
  async fetchCart(input, body) {
    const response = await fetch(input, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Accept: 'application/json',
      },
      body,
    });

    const data = await response.json();

    if (!response.ok) return { error: data.errors, data: null };

    return { error: null, data };
  }

  /**
   * Refresh the cart.
   * @description Uses /cart/update.js rather than ?sections= so the response carries
   * item_count alongside the rendered sections.
   */
  async refreshCart() {
    try {
      const body = JSON.stringify({
        sections: this.section,
        sections_url: window.location.pathname,
      });

      const { error, data } = await this.fetchCart(routes.cart_update_url, body);

      if (error) throw new Error(error);

      this.renderSections(data.sections);

      publish('cart:updated', {
        itemCount: data.item_count,
      });
    } catch (error) {
      window.toast.error(error.message);
    }
  }

  /**
   * Render the sections.
   * @description Replaces only the cart-dependent rows when [data-cart-lines] is present,
   * so cart-recommendations is not destroyed, does not re-fetch, and does not collapse the
   * drawer height on every change. Falls back to the full swap on older markup.
   */
  renderSections(sections) {
    const html = new DOMParser().parseFromString(sections[this.section], 'text/html');

    const next = html.querySelector('[data-cart-lines]');
    const current = this.querySelector('[data-cart-lines]');

    if (next && current) {
      current.replaceWith(next);
      return;
    }

    this.innerHTML = html.querySelector('line-items').innerHTML;
  }
}

customElements.define('line-items', LineItems);
